import React, { useState, useEffect, useRef } from 'react';
import { Usb, Network as NetworkIcon, Printer } from 'lucide-react';
import * as networkPrintApi from '@/services/networkPrintApi';
import NetworkPrinterDialog from '@/components/print/NetworkPrinterDialog';

// Per-screen USB / Network destination switch. Each screen passes a unique
// `screenKey` so its choice + selected network printer are remembered
// independently (combo stations stay on USB, others can use network).
// While mounted it publishes its choice as the app's active print target,
// which printerApi reads to route USB vs the network Print Bridge.
const PrintMethodToggle = ({ screenKey = 'default', dark = false, className = '', onMethodChange, autoNetworkWhenRouted = false }) => {
  const M = `lp_method::${screenKey}`;
  const P = `lp_printer::${screenKey}`;

  const [method, setMethodState] = useState(() => localStorage.getItem(M) || 'usb');
  const [netPrinter, setNetPrinter] = useState(() => {
    try { return JSON.parse(localStorage.getItem(P) || 'null'); } catch (_) { return null; }
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  // Tracks whether the worker explicitly picked a method this session, so a
  // slow central-route lookup can never override a choice made while it was
  // still in flight.
  const explicitChoiceRef = useRef(localStorage.getItem(M) != null);

  // Smart default: if the worker never explicitly picked a method on this
  // screen but an admin centrally routed this operation to a station, default
  // to Network so it "just works" with no per-device setup. We do NOT persist
  // this to `M` — it stays a default, so an explicit USB choice still wins.
  useEffect(() => {
    if (!autoNetworkWhenRouted) return;
    if (explicitChoiceRef.current || localStorage.getItem(M) != null) return;
    let cancelled = false;
    (async () => {
      try {
        const { assigned } = await networkPrintApi.getOperationRoute(screenKey);
        // Re-check after the await: the worker may have picked a method while
        // the request was in flight — that explicit choice always wins.
        if (cancelled || explicitChoiceRef.current || localStorage.getItem(M) != null) return;
        if (assigned) setMethodState('network');
      } catch (_) { /* keep usb default */ }
    })();
    return () => { cancelled = true; };
  }, [autoNetworkWhenRouted, M, screenKey]);

  // Publish this screen's choice as the active print target.
  useEffect(() => {
    localStorage.setItem('lp_active_method', method);
    localStorage.setItem('lp_active_op', screenKey);
    if (netPrinter) localStorage.setItem('lp_active_printer', JSON.stringify(netPrinter));
    else localStorage.removeItem('lp_active_printer');
    onMethodChange?.(method);
  }, [method, netPrinter, screenKey, onMethodChange]);

  const setMethod = async (m) => {
    explicitChoiceRef.current = true;
    setMethodState(m);
    localStorage.setItem(M, m);
    if (m === 'network' && !netPrinter) {
      // If this operation is centrally routed, no manual pick is needed.
      try {
        const { assigned } = await networkPrintApi.getOperationRoute(screenKey);
        if (!assigned) setPickerOpen(true);
      } catch (_) {
        setPickerOpen(true);
      }
    }
  };

  const onPicked = (sel) => {
    const s = { agentId: sel.agentId, printerName: sel.printerName, agentName: sel.agentName };
    setNetPrinter(s);
    localStorage.setItem(P, JSON.stringify(s));
    networkPrintApi.setPrintPreference({ agentId: sel.agentId, printerName: sel.printerName }).catch(() => {});
    setPickerOpen(false);
  };

  const activeCls = dark ? 'bg-white text-[#1A1F2E]' : 'bg-[#2D5F4F] text-white';
  const idleCls = dark ? 'text-[#E8DCC8] hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100';

  return (
    <div className={`inline-flex items-center gap-2 ${className}`} data-testid="label-print-method-bar">
      <div className={`flex rounded-md overflow-hidden border ${dark ? 'border-white/25' : 'border-gray-300'}`}>
        <button type="button" onClick={() => setMethod('usb')}
          className={`px-2.5 py-1 text-xs flex items-center gap-1 ${method === 'usb' ? activeCls : idleCls}`}
          data-testid="label-method-usb"><Usb className="w-3 h-3" /> USB</button>
        <button type="button" onClick={() => setMethod('network')}
          className={`px-2.5 py-1 text-xs flex items-center gap-1 ${method === 'network' ? activeCls : idleCls}`}
          data-testid="label-method-network"><NetworkIcon className="w-3 h-3" /> Network</button>
      </div>
      {method === 'network' && (
        <button type="button" onClick={() => setPickerOpen(true)}
          className={`text-xs flex items-center gap-1 underline underline-offset-2 ${dark ? 'text-cyan-300' : 'text-[#2D5F4F]'}`}
          data-testid="label-select-network-printer">
          <Printer className="w-3 h-3" /> {netPrinter ? netPrinter.printerName : 'Select printer'}
        </button>
      )}
      <NetworkPrinterDialog
        open={pickerOpen}
        onOpenChange={(o) => !o && setPickerOpen(false)}
        onConfirm={onPicked}
        title="Select a network printer"
      />
    </div>
  );
};

export default PrintMethodToggle;
